import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ArrowUpRight, X } from "lucide-react";
import { SHOW_STILLS, WALL_OF_ENT_LOGO } from "@/data/products";
import { optimizedImg, srcSetFor } from "@/lib/img";

// Tubi deep-link for the Substitute Teacher series — used by Class in
// Action CTA and the global Header "Watch" button.
const TUBI_SHOW_URL = "https://tubitv.com/series/300021533/substitute-teacher";

// Self-hosted trailer (uploaded to Emergent customer-assets CDN).
const TRAILER_URL =
  "https://customer-assets.emergentagent.com/job_wall-of-ent-show/artifacts/e1gfyzwe_S1_SUBT_TEASER_V4.mp4";

export default function ClassInAction() {
  const [trailerOpen, setTrailerOpen] = useState(false);
  const videoRef = useRef(null);

  // ESC closes the lightbox; restore body scroll on close.
  useEffect(() => {
    if (!trailerOpen) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setTrailerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Autoplay (muted-first works around iOS Safari restrictions; users can
    // tap the unmute control in the native controls bar).
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      const p = videoRef.current.play();
      if (p && p.catch) p.catch(() => {});
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [trailerOpen]);

  return (
    <section
      id="class-in-action"
      data-testid="class-in-action-section"
      className="relative bg-[var(--st-cream)] py-16 md:py-24 border-b-2 border-[#111] overflow-hidden"
    >
      <div className="cross-marker" style={{ top: 60, left: "10%" }} />
      <div className="cross-marker" style={{ bottom: 50, right: "10%" }} />

      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/60">
              Watch · Tubi TV
            </p>
            <h2 className="font-display uppercase leading-[0.85] tracking-tight text-[var(--st-maroon)] text-5xl md:text-7xl mt-2">
              CLASS IN
              <br />
              <span className="text-[var(--st-fg)]">
                ACT<span className="text-[var(--st-mustard)]">ION</span>
              </span>
            </h2>
          </div>
          <div className="flex flex-col md:items-end gap-3 max-w-md">
            <p className="font-body text-base text-[var(--st-fg)]/80 md:text-right">
              Tap in. Watch Substitute Teacher — only on Tubi.
            </p>
            <a
              href={TUBI_SHOW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="cia-watch-show-cta"
              className="self-start md:self-end inline-flex items-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-5 py-3 font-heading uppercase tracking-[0.18em] text-xs hover:bg-[var(--st-orange)] hover:text-[var(--st-fg)] transition-colors shadow-brutal-sm"
            >
              <Play size={14} fill="currentColor" /> Watch the Show on Tubi
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={() => setTrailerOpen(true)}
          data-testid="cia-watch-trailer"
          aria-label="Play Substitute Teacher trailer"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="group relative block w-full text-left border-2 border-[#111] overflow-hidden shadow-brutal bg-[var(--st-muted)]"
        >
          <div className="aspect-video overflow-hidden">
            <img
              src={optimizedImg(SHOW_STILLS[0], 1200)}
              srcSet={srcSetFor(SHOW_STILLS[0], [640, 960, 1200, 1600])}
              sizes="(max-width: 1024px) 100vw, 70vw"
              alt="Substitute Teacher set"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </div>

          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.span
              whileHover={{ scale: 1.08 }}
              className="relative w-20 h-20 md:w-28 md:h-28 flex items-center justify-center"
            >
              <span className="absolute inset-0 rotate-45 bg-[var(--st-mustard)] border-2 border-[#111] shadow-brutal-sm" />
              <Play
                size={36}
                fill="currentColor"
                className="relative text-[var(--st-fg)] translate-x-[2px]"
              />
            </motion.span>
          </div>

          {/* Corner badges */}
          <div className="absolute top-3 left-3 bg-[var(--st-maroon)] text-white px-2 py-0.5 font-heading uppercase tracking-[0.18em] text-[10px]">
            Watch Trailer
          </div>
          <div className="absolute top-3 right-3 bg-[var(--st-fg)] text-[var(--st-gold)] px-2 py-0.5 font-pixel text-[12px]">
            • REC
          </div>

          {/* Presented by chip — Wall of Entertainment */}
          <div
            data-testid="cia-presented-by"
            className="absolute bottom-14 md:bottom-16 left-4 flex items-center gap-2 bg-white border-2 border-[#111] px-3 py-1.5 shadow-brutal-sm"
          >
            <span className="font-heading uppercase tracking-[0.2em] text-[9px] md:text-[10px] text-[var(--st-maroon)]">
              Presented by
            </span>
            <img
              src={WALL_OF_ENT_LOGO}
              alt="Wall of Entertainment"
              className="h-4 md:h-5 w-auto"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-[var(--st-fg)]/85 backdrop-blur px-4 py-2.5">
            <p className="font-display uppercase tracking-tight text-base md:text-lg text-white">
              Substitute Teacher · The Know It Owls
            </p>
            <p className="font-heading uppercase tracking-[0.2em] text-[11px] text-[var(--st-gold)]">
              Streaming now
            </p>
          </div>
        </motion.button>
      </div>

      {/* TRAILER LIGHTBOX */}
      <AnimatePresence>
        {trailerOpen && (
          <motion.div
            data-testid="trailer-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setTrailerOpen(false)}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl aspect-video bg-black border-2 border-[#111] shadow-brutal"
            >
              <video
                ref={videoRef}
                data-testid="trailer-video"
                src={TRAILER_URL}
                controls
                autoPlay
                playsInline
                poster={optimizedImg(SHOW_STILLS[0], 1600)}
                className="w-full h-full object-contain bg-black"
              >
                Sorry — your browser doesn&apos;t support HTML5 video.
              </video>

              <button
                type="button"
                onClick={() => setTrailerOpen(false)}
                aria-label="Close trailer"
                data-testid="trailer-close"
                className="absolute -top-3 -right-3 w-10 h-10 flex items-center justify-center bg-[var(--st-mustard)] text-[var(--st-fg)] border-2 border-[#111] shadow-brutal-sm hover:bg-[var(--st-orange)] transition-colors"
              >
                <X size={18} />
              </button>

              <a
                href={TUBI_SHOW_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setTrailerOpen(false)}
                data-testid="trailer-watch-on-tubi"
                className="absolute left-0 right-0 -bottom-14 mx-auto w-fit inline-flex items-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-4 py-2.5 font-heading uppercase tracking-[0.18em] text-xs shadow-brutal-sm hover:bg-[var(--st-orange)] hover:text-[var(--st-fg)] transition-colors"
              >
                <Play size={14} fill="currentColor" /> Watch full series on Tubi
                <ArrowUpRight size={14} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
